<template>

    <app-layout :title="title">

        <todays-exercise-widget class="mb-8 sm:mb-16"
            :target="target">
        </todays-exercise-widget>

        <div class="flex flex-col w-full">

            <div class="flex justify-between mb-8">

                <div>

                    <h1 class="text-blue text-xl leading-normal sm:text-2xl sm:leading-normal font-bold sm:font-normal font-bold font-nexa mb-3.5">{{ title }}</h1>

                    <p class="hidden lg:inline text-sm sm:text-lg font-bold text-black text-opacity-60 mb-8">{{ description }}</p>

                </div>

                <button class="bg-white px-8 h-14 mt-auto leading-normal rounded-lg font-bold text-blue"
                    @click="$inertia.visit( route('change_exercises') )">

                    <font-awesome-icon class="mr-2 text-red"
                        :icon="['fas', 'heart']"
                    />

                    {{ __('Change Exercises') }}

                </button>

            </div>


            <div class="w-full grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-4 sm:mb-8 lg:mb-16">

                <div v-for="category of categories"
                    :key="category.id"
                    class="flex rounded-md items-center sm:rounded-lg h-[33px] sm:h-[60px] lg:h-[94px] bg-white hover:border-red border-transparent border-2 cursor-pointer"
                    :class="{
                        'border-red': category.active
                    }"
                    @click="handle_click_category(category)">

                    <div class="flex hidden lg:inline p-3 mr-1 xl:mr-8"
                        style="width: 80px;">

                        <img :src="category.icon_url"
                            class="h-full" />

                    </div>

                    <div class="flex w-full lg:w-8/12 flex-row sm:items-start lg:leading-none lg:flex-col sm:py-4.5">

                        <h3 class="text-blue w-full text-[11px] sm:text-lg md:text-xl font-bold ml-2 lg:ml-0 sm:mt-1">{{ category.name }}</h3>

                        <div class="sm:mt-auto mr-2 items-center flex lg:flex-row flex-row-reverse ">

                            <font-awesome-icon v-if="category.count > 0"
                                :icon="['fas', 'heart']"
                                class="text-xs sm:text-lg ml-2 sm:ml-0.5 md:ml-0 md:ml-4 mr-2 lg:mt-2 lg:ml-0 text-red"
                            />

                            <font-awesome-icon v-else
                                :icon="['far', 'heart']"
                                class="text-xs sm:text-lg ml-2 sm:ml-0.5 sm:ml-0 md:ml-4 mr-2 lg:mt-2 lg:ml-0 text-blue"
                            />

                            <span class="text-[9px] sm:text-base mt-0 lg:mt-2 sm:leading-tight font-bold text-blue select-none">{{ category.count }}</span>

                        </div>

                    </div>

                </div>

            </div>

            <div class="flex justify-between items-end mb-4 sm:mb-8">

                <h2 class="text-blue text-sm sm:text-2xl font-nexa">{{ active_category_name }}</h2>

                <p class="text-[10px] sm:text-base font-bold text-blue">{{ __(':count :video_text added to your exercises', {
                    'count': active_category_count,
                    'video_text': active_category_count === 1 ? __('video') : __('videoes')
                }) }}</p>

            </div>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-6">

                <div v-for="exercise of exercises"
                    :key="exercise.id"
                    class="flex h-20 sm:h-[172px] rounded-md sm:rounded-lg bg-white">


                    <div class="flex w-14 h-auto sm:w-[200px]">

                        <a class="w-full h-full"
                            :href="'exercise/' + exercise.slug">

                            <div class="h-full w-full rounded-l-lg overflow-hidden bg-cover bg-no-repeat bg-center"
                                :style="`background-image: url('${ exercise.thumbnail_url }');`">
                            </div>

                        </a>

                    </div>


                    <div class="flex flex-col w-full px-2 py-2 md:py-4 sm:px-8 lg:py-6">


                        <a :href="'exercise/' + exercise.slug">
                            <h3 class="text-black text-sm leading-[1.3em] sm:text-lg lg:text-xl xl:text-2xl xl:leading-[1.3em] sm:font-bold lg:leading-normal sm:leading-normal">{{ __(exercise.name) }}</h3>
                        </a>


                        <div class="mt-auto flex items-center cursor-pointer"
                            :class="{
                                'remove_favorite_button': exercise.active,
                                'add_favorite_button': ! exercise.active
                            }"
                             @click="handle_click_exercise_for_user_favorit(exercise)">

                            <font-awesome-icon
                                id="active_icon"
                                :icon="['fas', 'heart']"
                                class="text-xs sm:text-lg mr-2 text-red"
                                :class="{
                                    'hidden': ! exercise.active
                                }"
                            ></font-awesome-icon>

                            <font-awesome-icon
                                id="deactive_icon"
                                :icon="['far', 'heart']"
                                class="text-xs sm:text-lg mr-2 text-blue mt-0"
                                :class="{
                                    'hidden': exercise.active
                                }"
                            ></font-awesome-icon>

                            <p v-if="exercise.active"
                                class="block lg:inline text-xs lg:text-sm text-black text-opacity-60 select-none">

                                {{ __('Remove from favorites') }}

                            </p>

                            <p v-else
                                class="block lg:inline text-xs lg:text-sm text-black text-opacity-60 select-none">

                                {{ __('Add to favorites') }}

                            </p>

                        </div>

                    </div>

                </div>

            </div>

        </div>

    </app-layout>

</template>

<script>
import { defineComponent } from 'vue';
import AppLayout from '@/Layouts/AppLayout.vue';
import TodaysExerciseWidget from "@/Widgets/TodaysExerciseWidget";

export default defineComponent({

    name: 'YourExercises',

    components: {
        TodaysExerciseWidget,
        AppLayout,
    },

    props: {
        active_category_name: {
            type: String,
            default: () => {
                return 'Shoulder';
            }
        },
        active_category_count: {
            type: Number,
            default: 99
        },
        categories: {
            type: Array,
            default: () => {
                return [
                    {
                        id: 1,
                        slug: 'shoulder-rotations',
                        name: 'Shoulder',
                        icon_url: '/storage/shoulder-icon.svg',
                        active: true,
                        count: 4
                    },
                ];
            }
        },
        exercises: {
            type: Array,
            default: () => {
                return [
                    {
                        id: 1,
                        slug: 'shoulder-rotations1',
                        name: 'Shoulder rotations',
                        active: true,
                        thumbnail_url: '/storage/shoulder-rotations-exercise.jpg'
                    }
                ]
            }
        },
        target: {
            type: Object,
        }
    },

    computed: {

        title() {

            return this.__('Your exercises');

        },

        description() {

            return this.__('See your chosen exercises to train for, filtered by category');

        }

    },

    methods: {

        handle_click_category(category) {                                           let debug_id = Debug.init_method('handle_click_category',false);

            let
                route = this.route('your_exercises', {
                    active_category_id: category.id
                })
            ;                                                                       Debug.log(debug_id,241,{ route });

            this.$inertia.visit(
                route,
                {
                    only: ['categories', 'exercises', 'active_category_name', 'active_category_count']
                }
            );

        },


        handle_click_exercise_for_user_favorit(exercise) {                                                   let debug_id = Debug.init_method('handle_click_exercise_for_user_favorit',true);

            let
                route = this.route('exercise_user_favorite_toggle', {
                    exercise: exercise.slug
                })
            ;                                          Debug.log(debug_id,249,{route});


            this.$inertia
                .patch( route, {
                    preserveScroll: true,
                    onSuccess: this.reload_page,
                });

        },


        reload_page() {

            this.$inertia.reload({
                only: ['categories', 'exercises', 'active_category_count']
            });

        }

    },

});

</script>

<style scoped>

.remove_favorite_button:hover #active_icon  {
    display: none;
}

.remove_favorite_button:hover #deactive_icon  {
    display: block;
}

.add_favorite_button:hover #active_icon  {
    display: block;
}

.add_favorite_button:hover #deactive_icon  {
    display: none;
}

</style>
